import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { CheckCircle, XCircle, Warning, X } from "@phosphor-icons/react";

/**
 * AttendanceNudgeModal — V1.5.7.1.
 * Fires when the candidate drawer opens and there's at least one interview
 * whose end-time was > 30 min ago AND `attended` is still null. Shows a
 * blocking overlay with per-interview Attended / No-show quick actions so
 * recruiters get instant nudges (no need to scroll to the interview card).
 */
export default function AttendanceNudgeModal({ candidateId, overdue, onClose, onUpdated }) {
    const [saving, setSaving] = useState({});
    if (!overdue || overdue.length === 0) return null;

    const mark = async (iv, attended) => {
        setSaving((s) => ({ ...s, [iv.id]: true }));
        try {
            await api.patch(
                `/candidates/${candidateId}/interviews/${iv.id}/attended`,
                { attended },
            );
            toast.success(attended ? "Marked attended" : "Marked no-show");
            onUpdated?.();
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSaving((s) => ({ ...s, [iv.id]: false }));
        }
    };

    return (
        <div
            data-testid="attendance-nudge-modal"
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
        >
            <div className="bg-white border-2 border-amber-500 w-full max-w-md shadow-xl">
                <div className="flex items-center justify-between border-b border-amber-200 px-4 py-3 bg-amber-50">
                    <div className="flex items-center gap-2">
                        <Warning size={18} weight="fill" className="text-amber-600" />
                        <p className="text-xs font-black uppercase tracking-[0.15em] text-amber-900">
                            Attendance pending
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="attendance-nudge-close"
                        className="text-amber-800 hover:text-ink p-0.5"
                        aria-label="Dismiss"
                    >
                        <X size={14} weight="bold" />
                    </button>
                </div>
                <div className="p-4 space-y-3">
                    <p className="text-sm text-[#525252]">
                        {overdue.length === 1
                            ? "This candidate has one past interview without an attendance mark."
                            : `This candidate has ${overdue.length} past interviews without an attendance mark.`}
                        {" "}Please confirm so metrics stay accurate.
                    </p>
                    <div className="space-y-2">
                        {overdue.map((iv) => (
                            <div
                                key={iv.id}
                                data-testid={`attendance-nudge-row-${iv.id}`}
                                className="border border-[#E5E5E5] p-3"
                            >
                                <p className="text-xs font-bold text-ink mb-1">
                                    {iv.type ? iv.type.replace(/_/g, " ") : "Interview"}
                                </p>
                                <p className="text-[11px] text-[#525252]">
                                    {iv.end ? new Date(iv.end).toLocaleString() : "—"}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                    <button
                                        onClick={() => mark(iv, true)}
                                        disabled={!!saving[iv.id]}
                                        data-testid={`attendance-nudge-yes-${iv.id}`}
                                        className="flex-1 inline-flex items-center justify-center gap-1 py-2 text-[10px] font-black uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-60"
                                    >
                                        <CheckCircle size={11} weight="fill" /> Attended
                                    </button>
                                    <button
                                        onClick={() => mark(iv, false)}
                                        disabled={!!saving[iv.id]}
                                        data-testid={`attendance-nudge-no-${iv.id}`}
                                        className="flex-1 inline-flex items-center justify-center gap-1 py-2 text-[10px] font-black uppercase tracking-wider bg-[#DC2626] hover:bg-[#B91C1C] text-white transition-colors disabled:opacity-60"
                                    >
                                        <XCircle size={11} weight="fill" /> No-show
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="border-t border-[#E5E5E5] px-4 py-3 flex justify-end">
                    <button
                        onClick={onClose}
                        data-testid="attendance-nudge-remind-later"
                        className="text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-ink"
                    >
                        Remind me later
                    </button>
                </div>
            </div>
        </div>
    );
}
